import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, CheckCircle, Copy } from 'lucide-react';
import { adminApi } from '@/lib/api/admin';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/orgs/new')({
  component: NewOrgPage,
});

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring';

function NewOrgPage() {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logoURL, setLogoURL] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ inviteURL: string } | null>(null);
  const navigate = useNavigate();

  const deriveSlug = (n: string) =>
    n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleNameChange = (v: string) => {
    setName(v);
    if (!slug || slug === deriveSlug(name)) setSlug(deriveSlug(v));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await adminApi.createOrg({
        name, slug, logo_url: logoURL || undefined,
        admin: { email: adminEmail },
      });
      setCreated({ inviteURL: r.invite_url });
      toast.success('Organization created. Org admin invite sent.');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="mx-auto max-w-xl space-y-5">
        <div className="flex items-center gap-3">
          <CheckCircle className="h-8 w-8 text-emerald-500" />
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Organization created</h1>
            <p className="text-[13px] text-muted-foreground">Invite email sent. Share this link if needed:</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <code className="flex-1 overflow-x-auto text-[12px] text-foreground">{created.inviteURL}</code>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(created.inviteURL); toast.success('Copied'); }}
            className="rounded p-1.5 hover:bg-muted"
          >
            <Copy className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/orgs' })}
            className="rounded-lg bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground hover:bg-primary/90"
          >
            Done
          </button>
          <button
            type="button"
            onClick={() => setCreated(null)}
            className="rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-foreground hover:bg-muted"
          >
            Create another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <Link to="/admin/orgs" className="mb-3 flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Organizations
        </Link>
        <h1 className="text-2xl font-heading font-bold text-foreground">New Organization</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Creates the organization and sends an invite to its first org admin.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg border border-border bg-background p-5 shadow-sm space-y-4">
          <h2 className="text-[13px] font-semibold text-foreground">Organization details</h2>
          <Field label="Name">
            <input required placeholder="e.g. IIT Bombay" value={name} onChange={(e) => handleNameChange(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Slug">
            <input required placeholder="e.g. iit-bombay" value={slug} onChange={(e) => setSlug(e.target.value)} className={`${inputCls} font-mono`} />
            <p className="mt-1 text-[11px] text-muted-foreground">Used in URLs. Lowercase letters, numbers, and hyphens only.</p>
          </Field>
          <Field label="Logo URL (optional)">
            <input placeholder="https://…" value={logoURL} onChange={(e) => setLogoURL(e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="rounded-lg border border-border bg-background p-5 shadow-sm space-y-4">
          <div>
            <h2 className="text-[13px] font-semibold text-foreground">Org admin</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">This person will receive an invite and become the organization's admin. They'll choose their username when they accept.</p>
          </div>
          <Field label="Email">
            <input required type="email" placeholder="admin@college.edu" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-primary px-5 py-2.5 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create & send invite'}
          </button>
          <Link
            to="/admin/orgs"
            className="rounded-lg border border-border px-5 py-2.5 text-[13px] font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
